import React, { useEffect, useState } from 'react';

import { useParams } from "react-router-dom";

import { useLocalization } from '@progress/kendo-react-intl';
import { Form, Field, FormElement } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import { Error } from "@progress/kendo-react-labels";
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import {
    emailValidator,
    requiredValidator,
    passwordMatchValidator,
    partsCodeValidator,
    address1Validator,
    cityValidator,
    stateValidator,
    zipValidator,
    phoneValidator,
    newPasswordValidator,
    dealerCodeValidator
} from '../validators';

import { AppContext } from '../AppContext'
import { useServices } from 'src/services';
import { useStores } from 'src/stores';
import { Input } from 'src/components/Form/Input';
import { useHistory } from 'react-router';
import { Loader } from '@progress/kendo-react-indicators';
import { Dealer } from 'src/utils/classes';
import moment from 'moment';

type DealerParams = {
    dealer_id: string;
};

const dealerTypes = [
    { value: 'F', text: 'Ford' },
    { value: 'FL', text: 'Ford/Lincoln' }
];

const dealerRegions = [
    { value: 'CE', text: 'Central Market Area' },
    { value: 'EMA', text: 'East Market Area' },
    { value: 'GL', text: 'Great Lakes Market Area' },
    { value: 'SE', text: 'Southeast Market Area' },
    { value: 'W', text: 'West Market Area' },
]

const DealerFormScreen = () => {
    const { ...formValues } = React.useContext(AppContext);
    const localizationService = useLocalization();

    const { d4ur } = useServices();
    const { login } = useStores();
    let history = useHistory();
    const { dealer_id } = useParams<DealerParams>();

    const [formError, setFormError] = useState<string>();

    const [processing, setProcessing] = useState<boolean>(true);
    const [dealer, setDealer] = useState<Dealer>();

    // Dealer Notes state management
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');
    
    // Modal states
    const [dealerNotesModalVisible, setDealerNotesModalVisible] = useState<boolean>(false);
    const [selectedDealerNotes, setSelectedDealerNotes] = useState<any[]>([]);
    const [selectedDealerName, setSelectedDealerName] = useState<string>('');
    
    // Add dealer note modal states
    const [addDealerNoteModalVisible, setAddDealerNoteModalVisible] = useState<boolean>(false);
    const [selectedDealerForNote, setSelectedDealerForNote] = useState<any>(null);
    const [selectedDealerNameForNote, setSelectedDealerNameForNote] = useState<string>('');
    const [newDealerNoteText, setNewDealerNoteText] = useState<string>('');
    const [showAddDealerNoteInModal, setShowAddDealerNoteInModal] = useState<boolean>(false);
    const [newDealerNoteTextInModal, setNewDealerNoteTextInModal] = useState<string>('');
    const [selectedDealerForNoteId, setSelectedDealerForNoteId] = useState<number | null>(null);
    
    // Edit note states
    const [editingDealerNoteIndex, setEditingDealerNoteIndex] = useState<number | null>(null);
    const [editedDealerNoteText, setEditedDealerNoteText] = useState<string>('');
    const [originalDealerNoteContent, setOriginalDealerNoteContent] = useState<string>('');
    
    // Local notes state for instant display updates
    const [localDealerNotes, setLocalDealerNotes] = useState<any[]>([]);

    // Helper function to update local notes immediately
    const updateLocalNotes = (notes: any[]) => {
        setLocalDealerNotes([...notes]);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (dealer_id !== '0') {
            d4ur.getDealer(dealer_id).then((ret) => {
                console.log('RET DEALER', ret);

                const { dealer } = (ret as D4URxFetchResult).result;

                setDealer(new Dealer(dealer));
                setProcessing(false);
            });
        } else {
            setDealer({
                dealer_id: 0,
            } as Dealer);
            setProcessing(false);
        }
    }, [dealer_id, d4ur])

    // Sync local dealer notes when dealer data changes
    useEffect(() => {
        if (dealer && dealer.dealer_notes) {
            setLocalDealerNotes(dealer.dealer_notes);
        } else {
            setLocalDealerNotes([]);
        }
    }, [dealer]);

    // Keep modal state in sync with local notes
    useEffect(() => {
        if (dealerNotesModalVisible) {
            setSelectedDealerNotes(localDealerNotes);
        }
    }, [localDealerNotes, dealerNotesModalVisible]);

    // Control body overflow when modals are open
    useEffect(() => {
        if (dealerNotesModalVisible || addDealerNoteModalVisible) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [dealerNotesModalVisible, addDealerNoteModalVisible]);

    // Add CSS styling for modal headers
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .k-dialog .k-window-titlebar {
                background-color: #007bff !important;
                border-color: #007bff !important;
            }
            .k-dialog .k-window-title {
                color: white !important;
            }
            .k-dialog .k-window-titlebar .k-window-action {
                color: white !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Function to reload dealer data
    const loadDealer = async () => {
        if (dealer_id !== '0') {
            const ret = await d4ur.getDealer(dealer_id);
            console.log('loadDealer response:', ret);
            if (ret && (ret as any).result && (ret as any).result.dealer) {
                const { dealer } = (ret as any).result;
                setDealer(new Dealer(dealer));
            } else {
                console.error('loadDealer: No valid dealer data found in response');
            }
        }
    };

    const onSubmit = React.useCallback(
        async (dataItem) => {
            setProcessing(true);

            if (!dataItem.confirm_password) {
                delete dataItem.password;
            }

            // console.log('gotta update dealer', dataItem);
            const call: any = await d4ur.updateDealer(dataItem);
            if (call.result?.errors) {
                console.log('[DealerFormScreen]', 'Update Dealer Error');
                setFormError(call.result.errors.join('\n'));
                setProcessing(false);
            } else {
                console.log('[DealerFormScreen]', 'dealer updated');
                history.push('/retailers');
            }
        },
        [d4ur, history]
    );

    // Handler functions for dealer notes
    const handleSaveDealerNote = async () => {
        if (!newDealerNoteText.trim() || !dealer?.dealer_id) return;
        
        setIsLoading(true);
        setErrorMessage('');
        
        try {
            const ret = await d4ur.addDealerNoteByDealerId(
                dealer.dealer_id,
                newDealerNoteText.trim(),
                login.user?.credentials?.email || '',
                login.user?.credentials?.display_name || ''
            );
            
            if ((ret as any).response?.status === 200) {
                setNewDealerNoteText('');
                
                // Update dealer state immediately for instant UI update
                if (dealer) {
                    const newNote = {
                        note: newDealerNoteText.trim(),
                        created_at: moment().format("MMM DD YYYY hh:mm A"),
                        created_by: login.user?.credentials?.email || '',
                        created_by_name: login.user?.credentials?.display_name || ''
                    };
                    
                    let updatedNotes: any[] = [];
                    const currentNotes = dealer.dealer_notes;
                    
                    if (typeof currentNotes === 'string') {
                        try {
                            updatedNotes = JSON.parse(currentNotes);
                        } catch {
                            updatedNotes = [];
                        }
                    } else if (Array.isArray(currentNotes)) {
                        updatedNotes = [...currentNotes];
                    }
                    
                    updatedNotes.push(newNote);
                    
                    // Update local state immediately for instant display
                    updateLocalNotes(updatedNotes);
                    
                    setDealer(prevDealer => ({
                        ...prevDealer!,
                        dealer_notes: updatedNotes
                    }));
                }
                
                await loadDealer(); // Reload dealer data to get updated notes from server
                // setSuccessMessage('Dealer note added successfully!');
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setErrorMessage('Failed to add dealer note. Please try again.');
            }
        } catch (error) {
            console.error('Error adding dealer note:', error);
            setErrorMessage('Failed to add dealer note. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveDealerNoteInModal = async () => {
        if (!newDealerNoteTextInModal.trim() || !selectedDealerForNoteId) return;
        
        setIsLoading(true);
        setErrorMessage('');
        
        try {
            const ret = await d4ur.addDealerNoteByDealerId(
                selectedDealerForNoteId,
                newDealerNoteTextInModal.trim(),
                login.user?.credentials?.email || '',
                login.user?.credentials?.display_name || ''
            );
            
            if ((ret as any).response?.status === 200) {
                setNewDealerNoteTextInModal('');
                setShowAddDealerNoteInModal(false);
                setAddDealerNoteModalVisible(false);
                
                // Update dealer state immediately for instant UI update
                if (dealer) {
                    const newNote = {
                        note: newDealerNoteTextInModal.trim(),
                        created_at: moment().format("MMM DD YYYY hh:mm A"),
                        created_by: login.user?.credentials?.email || '',
                        created_by_name: login.user?.credentials?.display_name || ''
                    };
                    
                    let updatedNotes: any[] = [];
                    const currentNotes = dealer.dealer_notes;
                    
                    if (typeof currentNotes === 'string') {
                        try {
                            updatedNotes = JSON.parse(currentNotes);
                        } catch {
                            updatedNotes = [];
                        }
                    } else if (Array.isArray(currentNotes)) {
                        updatedNotes = [...currentNotes];
                    }
                    
                    updatedNotes.push(newNote);
                    
                    // Update local state immediately for instant display
                    updateLocalNotes(updatedNotes);
                    
                    setDealer(prevDealer => ({
                        ...prevDealer!,
                        dealer_notes: updatedNotes
                    }));
                }
                
                await loadDealer(); // Reload dealer data to get updated notes from server
                // setSuccessMessage('Dealer note added successfully!');
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setErrorMessage('Failed to add dealer note. Please try again.');
            }
        } catch (error) {
            console.error('Error adding dealer note:', error);
            setErrorMessage('Failed to add dealer note. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveEditedDealerNote = async (originalTimestamp: string) => {
        if (!editedDealerNoteText.trim() || editingDealerNoteIndex === null || !selectedDealerForNoteId) return;
        
        setIsLoading(true);
        setErrorMessage('');
        
        try {
            const ret = await d4ur.updateDealerNoteByDealerId(
                selectedDealerForNoteId,
                originalTimestamp,
                originalDealerNoteContent,
                editedDealerNoteText.trim()
            );
            
            if ((ret as any).response?.status === 200) {
                // Update dealer state immediately for instant UI update
                if (dealer && editingDealerNoteIndex !== null) {
                    let updatedNotes: any[] = [];
                    const currentNotes = dealer.dealer_notes;
                    
                    if (typeof currentNotes === 'string') {
                        try {
                            updatedNotes = JSON.parse(currentNotes);
                        } catch {
                            updatedNotes = [];
                        }
                    } else if (Array.isArray(currentNotes)) {
                        updatedNotes = [...currentNotes];
                    }
                    
                    // Update the specific note
                    if (updatedNotes[editingDealerNoteIndex]) {
                        updatedNotes[editingDealerNoteIndex] = {
                            ...updatedNotes[editingDealerNoteIndex],
                            note: editedDealerNoteText.trim()
                        };
                    }
                    
                    // Update local state immediately for instant display
                    updateLocalNotes(updatedNotes);
                    
                    setDealer(prevDealer => ({
                        ...prevDealer!,
                        dealer_notes: updatedNotes
                    }));
                    
                    // Update modal state as well
                    setSelectedDealerNotes(updatedNotes);
                }
                
                setEditingDealerNoteIndex(null);
                setEditedDealerNoteText('');
                setOriginalDealerNoteContent('');
                await loadDealer(); // Reload dealer data to get updated notes from server
                
                // setSuccessMessage('Dealer note updated successfully!');
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setErrorMessage('Failed to update dealer note. Please try again.');
            }
        } catch (error) {
            console.error('Error updating dealer note:', error);
            setErrorMessage('Failed to update dealer note. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteDealerNote = async (originalTimestamp: string, noteContent: string, noteIndex: number) => {
        if (!selectedDealerForNoteId) return;
        
        setIsLoading(true);
        setErrorMessage('');
        
        try {
            const ret = await d4ur.deleteDealerNoteByDealerId(
                selectedDealerForNoteId,
                originalTimestamp,
                noteContent
            );
            
            if ((ret as any).response?.status === 200) {
                // Update local state immediately for instant display
                const updatedNotes = localDealerNotes.filter((_, index) => index !== noteIndex);
                updateLocalNotes(updatedNotes);
                
                // Update modal state as well
                setSelectedDealerNotes(updatedNotes);
                
                await loadDealer(); // Reload dealer data to get updated notes from server
                
                // setSuccessMessage('Dealer note deleted successfully!');
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setErrorMessage('Failed to delete dealer note. Please try again.');
            }
        } catch (error) {
            console.error('Error deleting dealer note:', error);
            setErrorMessage('Failed to delete dealer note. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const generateDealerNotes = (dealer_notes: any) => {
        if (!dealer_notes) {
            return 'No Notes'
        }
        
        let parsedNotes = [];
        try {
            if (typeof dealer_notes === 'string') {
                // Clean up the string and check if it's valid JSON
                const cleanedNotes = dealer_notes.trim();
                if (cleanedNotes === '' || cleanedNotes === 'null' || cleanedNotes === 'undefined' || cleanedNotes === '[]') {
                    return 'No Notes';
                } else if (cleanedNotes.startsWith('[') || cleanedNotes.startsWith('{')) {
                    parsedNotes = JSON.parse(cleanedNotes);
                } else {
                    console.warn('dealer_notes not in JSON format:', cleanedNotes);
                    return 'No Notes';
                }
            } else if (Array.isArray(dealer_notes)) {
                parsedNotes = dealer_notes;
            } else {
                return 'No Notes';
            }
        } catch (e) {
            console.error('Failed to parse dealer_notes:', e);
            return 'No Notes';
        }

        if (!Array.isArray(parsedNotes) || parsedNotes.length === 0) {
            return 'No Notes';
        }

        const formattedNotes = parsedNotes
            .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
            .map((note: any) => {
                const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
                return `${formattedDateTime} - ${note.created_by}: ${note.note}`;
            });

        return (
            <td>
                {formattedNotes.map((note: string, index: number) => (
                    <div key={index} style={{ borderBottom: "1px solid lightgrey", fontSize: 10, color: 'grey' }}>{note}</div>
                ))}
            </td>
        );
    }

    return (
        <div id="Dealer" className="dealer-page">
            <div className="card-container" style={{ maxWidth: 700 }}>
                {
                    processing ? <div style={{ textAlign: 'center' }}><Loader type="infinite-spinner" size="large" /></div> :
                        <>
                            {
                                dealer?.dealer_id ?
                                    <h1>Edit Dealer</h1>
                                    :
                                    <h1>Create Dealer</h1>
                            }
                            <div className="card-component">
                                <br />
                                <h3>{dealer?.dealer_name}</h3>
                                <hr />
                                <Form
                                    onSubmit={onSubmit}
                                    initialValues={{
                                        ...formValues,
                                        address2: '',
                                        ...dealer,
                                    }}
                                    render={(formRenderProps) => (
                                        <FormElement horizontal={true} style={{ maxWidth: 700 }}>
                                            {formError && <Error>{formError}</Error>}
                                            <Field
                                                id={'dealer_name'}
                                                name={'dealer_name'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.dealer_name', 'dealer_name')}
                                                validator={requiredValidator}
                                                component={Input}
                                                maxLength={50}
                                            />
                                            <Field
                                                id={'parts_code'}
                                                name={'parts_code'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.sales_code', 'sales_code')}
                                                validator={dealer_id !== '0' ? undefined : partsCodeValidator}
                                                component={Input}
                                                disabled={dealer_id !== '0'}
                                                minLength={5}
                                                maxLength={5}
                                            />
                                            <Field
                                                id={'address'}
                                                name={'address'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.address', 'address')}
                                                validator={address1Validator}
                                                component={Input}
                                            />
                                            <Field
                                                id={'address2'}
                                                name={'address2'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.address2', 'address2')}
                                                component={Input}
                                            />
                                            <Field
                                                id={'city'}
                                                name={'city'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.city', 'city')}
                                                validator={cityValidator}
                                                component={Input}
                                            />
                                            <Field
                                                id={'state'}
                                                name={'state'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.state', 'state')}
                                                validator={stateValidator}
                                                component={Input}
                                            />
                                            <Field
                                                id={'zip'}
                                                name={'zip'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.zip', 'zip')}
                                                validator={zipValidator}
                                                component={Input}
                                            />
                                            {/* Dealer phone hidden for now
                                            <Field
                                                id={'phone'}
                                                name={'phone'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.dealershipPhone', 'dealershipPhone')}
                                                validator={phoneValidator}
                                                component={Input}
                                            />
                                            */}
                                            <Field
                                                id={'primary_contact'}
                                                name={'primary_contact'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.primary_contact', 'primary_contact')}
                                                validator={requiredValidator}
                                                component={Input}
                                                maxLength={50}
                                            />
                                            <Field
                                                id={'contact_phone'}
                                                name={'contact_phone'}
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.contact_phone', 'contact_phone')}
                                                validator={phoneValidator}
                                                component={Input}
                                            />
                                            <Field
                                                id={'contact_email'}
                                                name={'contact_email'}
                                                type={'email'}
                                                autoComplete="username"
                                                placeholder={'e.g.: peter@gmail.com'}
                                                label={localizationService.toLanguageString('custom.contactEmail', 'Email')}
                                                validator={emailValidator}
                                                required={true}
                                                component={Input}
                                            />
                                                <Field
                                                id={"secondary_contact"}
                                                name={"secondary_contact"}
                                                label={localizationService.toLanguageString(
                                                    "custom.secondary_contact",
                                                    "Secondary Contact"
                                                )}
                                                component={Input}
                                                required={true}
                                                />
                                                <Field
                                                id={"secondary_contact_email"}
                                                name={"secondary_contact_email"}
                                                label={localizationService.toLanguageString(
                                                    "custom.secondary_contact_email",
                                                    "Secondary Contact Email"
                                                )}
                                                validator={emailValidator}
                                                component={Input}
                                                required={true}
                                                />
                                                <Field
                                                id={"secondary_contact_phone"}
                                                name={"secondary_contact_phone"}
                                                label={localizationService.toLanguageString(
                                                    "custom.secondary_contact_phone",
                                                    "Secondary Contact Phone"
                                                )}
                                                validator={phoneValidator}
                                                component={Input}
                                                required={true}
                                                />
                                                <Field
                                                id={"tertiary_contact"}
                                                name={"tertiary_contact"}
                                                label={localizationService.toLanguageString(
                                                    "custom.tertiary_contact",
                                                    "Tertiary Contact"
                                                )}
                                                component={Input}
                                                required={true}
                                                />
                                                <Field
                                                id={"tertiary_contact_email"}
                                                name={"tertiary_contact_email"}
                                                label={localizationService.toLanguageString(
                                                    "custom.tertiary_contact_email",
                                                    "Tertiary Contact Email"
                                                )}
                                                validator={emailValidator}
                                                component={Input}
                                                required={true}
                                                />
                                                <Field
                                                id={"tertiary_contact_phone"}
                                                name={"tertiary_contact_phone"}
                                                label={localizationService.toLanguageString(
                                                    "custom.tertiary_contact_phone",
                                                    "Tertiary Contact Phone"
                                                )}
                                                validator={phoneValidator}
                                                component={Input}
                                                required={true}
                                                />
                                            <Field
                                                id={'password'}
                                                name={'password'}
                                                autoComplete="new-password"
                                                type={'password'}
                                                label={localizationService.toLanguageString('custom.password', 'Password')}
                                                validator={newPasswordValidator}
                                                component={Input}
                                            />
                                            <Field
                                                id={'confirm_password'}
                                                name={'confirm_password'}
                                                type={'password'}
                                                autoComplete="new-password"
                                                // type={'input'}
                                                label={localizationService.toLanguageString('custom.confirmPassword', 'Confirm Password')}
                                                validator={passwordMatchValidator}
                                                component={Input}
                                            />
                                            {/* ARCHIVED (D4URX-2752): Dealer Notes are hidden everywhere in the admin; code kept so it can be restored.
                                               To restore: delete these two header lines and the closing comment line, and un-escape any star-backslash-slash.
                                            {/* Interactive Dealer Notes Section - Inline with other fields *\/}
                                            <div style={{ marginTop: 20, marginBottom: 20, display: 'flex', alignItems: 'center' }}>
                                                <label style={{ fontSize: 14, fontWeight: 'bold', marginRight: 10 }}>
                                                    {localizationService.toLanguageString('custom.dealerNotes', 'Dealer Notes')}:
                                                </label>
                                                
                                                {(() => {
                                                    const dealerNotes = localDealerNotes.length > 0 ? localDealerNotes : dealer?.dealer_notes;
                                                    let notesCount = 0;
                                                    let parsedNotes: any[] = [];

                                                    if (dealerNotes) {
                                                        if (typeof dealerNotes === 'string') {
                                                            try {
                                                                parsedNotes = JSON.parse(dealerNotes);
                                                                notesCount = Array.isArray(parsedNotes) ? parsedNotes.length : 0;
                                                            } catch {
                                                                notesCount = 0;
                                                            }
                                                        } else if (Array.isArray(dealerNotes)) {
                                                            parsedNotes = dealerNotes;
                                                            notesCount = dealerNotes.length;
                                                        }
                                                    }

                                                    const openDealerNotesModal = () => {
                                                        setSelectedDealerNotes(localDealerNotes.length > 0 ? localDealerNotes : parsedNotes || []);
                                                        setSelectedDealerName(dealer?.dealer_name || '');
                                                        setSelectedDealerForNoteId(dealer?.dealer_id || null);
                                                        setShowAddDealerNoteInModal(false);
                                                        setNewDealerNoteTextInModal('');
                                                        setDealerNotesModalVisible(true);
                                                    };

                                                    const openAddDealerNoteModal = () => {
                                                        setSelectedDealerForNote(dealer);
                                                        setSelectedDealerNameForNote(dealer?.dealer_name || '');
                                                        setSelectedDealerForNoteId(dealer?.dealer_id || null);
                                                        setNewDealerNoteTextInModal('');
                                                        setAddDealerNoteModalVisible(true);
                                                    };

                                                    if (notesCount > 0) {
                                                        return (
                                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                                <span style={{ fontSize: "14px", marginRight: "8px" }}>Notes({notesCount})</span>
                                                                <span
                                                                    className={'k-icon k-i-edit'}
                                                                    style={{ color: '#666', fontSize: '14px', cursor: 'pointer' }}
                                                                    onClick={openDealerNotesModal}
                                                                    title="Edit notes"
                                                                />
                                                            </div>
                                                        );
                                                    } else {
                                                        return (
                                                            <button
                                                                className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                                                                style={{ 
                                                                    backgroundColor: "#007bff",
                                                                    borderColor: "#007bff",
                                                                    color: "white",
                                                                    marginLeft: "8px"
                                                                }}
                                                                onClick={openAddDealerNoteModal}
                                                                title="Add note"
                                                            >
                                                                Add Note
                                                            </button>
                                                        );
                                                    }
                                                })()}
                                            </div>
                                            
                                            {/* Notes Preview - Show up to 5 notes *\/}
                                            {(() => {
                                                const dealerNotes = localDealerNotes.length > 0 ? localDealerNotes : dealer?.dealer_notes;
                                                let parsedNotes: any[] = [];

                                                if (dealerNotes) {
                                                    if (typeof dealerNotes === 'string') {
                                                        try {
                                                            parsedNotes = JSON.parse(dealerNotes);
                                                        } catch {
                                                            parsedNotes = [];
                                                        }
                                                    } else if (Array.isArray(dealerNotes)) {
                                                        parsedNotes = dealerNotes;
                                                    }
                                                }

                                                if (parsedNotes.length > 0) {
                                                    // Show up to 5 most recent notes
                                                    const notesToShow = parsedNotes
                                                        .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at))) // Sort by oldest first (matching Event.tsx)
                                                        .slice(0, 5);

                                                    return (
                                                        <div style={{ marginLeft: "0px" }}>
                                                            {notesToShow.map((note: any, index: number) => {
                                                                const formattedDateTime = moment(note.created_at).format("MMM DD, YYYY hh:mm A");
                                                                return (
                                                                    <div key={index} style={{ 
                                                                        borderBottom: "1px solid lightgrey", 
                                                                        paddingBottom: "4px", 
                                                                        marginBottom: "4px" 
                                                                    }}>
                                                                        {formattedDateTime} - {note.created_by_name || note.created_by}: {note.note}
                                                                    </div>
                                                                );
                                                            })}
                                                            {parsedNotes.length > 5 && (
                                                                <div style={{ 
                                                                    fontStyle: "italic", 
                                                                    color: "#666",
                                                                    marginTop: "8px"
                                                                }}>
                                                                    ... and {parsedNotes.length - 5} more notes
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            */}
                                            
                                            {/* Messages */}
                                            {errorMessage && (
                                                <div style={{ color: 'red', fontSize: 12, marginTop: 5 }}>
                                                    {errorMessage}
                                                </div>
                                            )}
                                            {successMessage && (
                                                <div style={{ color: 'green', fontSize: 12, marginTop: 5 }}>
                                                    {successMessage}
                                                </div>
                                            )}
                                            <hr />
                                            <div className={'card-buttons'}>
                                                <Button
                                                    className="ford-blue-btn"
                                                    primary={true}
                                                    type={'submit'}
                                                    disabled={!formRenderProps.allowSubmit}
                                                >
                                                    {dealer?.dealer_id ? localizationService.toLanguageString('custom.update', 'update') : localizationService.toLanguageString('custom.save', 'save')}
                                                </Button>
                                            </div>
                                        </FormElement>
                                    )}
                                />
                            </div>
                        </>
                }
            </div>

            {/* Add Dealer Note Modal */}
            {addDealerNoteModalVisible && (
                <Dialog
                    title="Add Dealer Note"
                    onClose={() => setAddDealerNoteModalVisible(false)}
                    width={500}
                    height={300}
                >
                    <div style={{ padding: '20px' }}>
                        <div style={{ marginBottom: 15 }}>
                            <strong>Dealer: {selectedDealerNameForNote}</strong>
                        </div>
                        <div style={{ marginBottom: 15 }}>
                            <label>Note:</label>
                            <textarea
                                value={newDealerNoteTextInModal}
                                onChange={(e) => setNewDealerNoteTextInModal(e.target.value)}
                                placeholder="Enter dealer note..."
                                style={{
                                    width: '100%',
                                    height: '100px',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    marginTop: '5px'
                                }}
                            />
                        </div>
                        {errorMessage && (
                            <div style={{ color: 'red', fontSize: 12, marginBottom: 10 }}>
                                {errorMessage}
                            </div>
                        )}
                    </div>
                    <DialogActionsBar>
                        <Button
                            primary={true}
                            onClick={handleSaveDealerNoteInModal}
                            disabled={!newDealerNoteTextInModal.trim() || isLoading}
                        >
                            {isLoading ? 'Saving...' : 'Save Note'}
                        </Button>
                        <Button onClick={() => setAddDealerNoteModalVisible(false)}>
                            Cancel
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {/* Dealer Notes Modal */}
            {dealerNotesModalVisible && (
                <Dialog
                    title={`Dealer Notes - ${selectedDealerName}`}
                    onClose={() => {
                        setDealerNotesModalVisible(false);
                        setShowAddDealerNoteInModal(false);
                        setNewDealerNoteTextInModal('');
                        setSelectedDealerForNoteId(null);
                    }}
                    width={600}
                    height={showAddDealerNoteInModal ? 550 : 400}
                >
                    <div style={{ padding: "20px" }}>
                        {/* Add Note Section - Moved to top */}
                        {showAddDealerNoteInModal ? (
                            <div style={{ 
                                marginBottom: "20px", 
                                padding: "15px", 
                                border: "1px solid #007bff", 
                                borderRadius: "5px",
                                backgroundColor: "#f8f9fa"
                            }}>
                                <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
                                    Add New Dealer Note:
                                </label>
                                <textarea
                                    value={newDealerNoteTextInModal}
                                    onChange={(e) => setNewDealerNoteTextInModal(e.target.value)}
                                    placeholder="Enter your dealer note here..."
                                    style={{
                                        width: "100%",
                                        height: "80px",
                                        padding: "10px",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        fontSize: "14px",
                                        resize: "vertical",
                                        boxSizing: "border-box"
                                    }}
                                />
                                <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
                                    <Button
                                        className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                                        style={{
                                            backgroundColor: "#007bff",
                                            borderColor: "#007bff",
                                            color: "white"
                                        }}
                                        onClick={handleSaveDealerNoteInModal}
                                        disabled={!newDealerNoteTextInModal.trim()}
                                    >
                                        Save Note
                                    </Button>
                                    <Button
                                        className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                                        onClick={() => {
                                            setShowAddDealerNoteInModal(false);
                                            setNewDealerNoteTextInModal('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <Button
                                className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                                style={{ 
                                    marginBottom: "20px",
                                    backgroundColor: "#007bff",
                                    borderColor: "#007bff"
                                }}
                                onClick={() => setShowAddDealerNoteInModal(true)}
                            >
                                Add Note
                            </Button>
                        )}

                        {selectedDealerNotes.length > 0 ? (
                            selectedDealerNotes.map((note, index) => (
                                <div 
                                    key={index} 
                                    style={{ 
                                        marginBottom: "20px", 
                                        padding: "15px", 
                                        border: "1px solid #ddd", 
                                        borderRadius: "5px",
                                        backgroundColor: "#f9f9f9"
                                    }}
                                >
                                    {editingDealerNoteIndex === index ? (
                                        // Edit mode - stack everything vertically
                                        <div>
                                            <div style={{ marginBottom: "8px" }}>
                                                <strong>Note:</strong>
                                                <textarea
                                                    value={editedDealerNoteText}
                                                    onChange={(e) => setEditedDealerNoteText(e.target.value)}
                                                    style={{
                                                        width: "100%",
                                                        height: "80px",
                                                        padding: "8px",
                                                        border: "1px solid #ccc",
                                                        borderRadius: "4px",
                                                        fontSize: "14px",
                                                        marginTop: "5px",
                                                        boxSizing: "border-box"
                                                    }}
                                                />
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
                                                <span style={{ marginRight: "15px" }}>
                                                    <strong>Created:</strong> {note.created_at}
                                                </span>
                                                <span>
                                                    <strong>By:</strong> {note.created_by}
                                                </span>
                                            </div>
                                            <div style={{ display: "flex", gap: "10px" }}>
                                                <Button
                                                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-primary"
                                                    style={{
                                                        backgroundColor: "#007bff",
                                                        borderColor: "#007bff",
                                                        color: "white"
                                                    }}
                                                    onClick={() => handleSaveEditedDealerNote(note.created_at)}
                                                    disabled={!editedDealerNoteText.trim()}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    className="k-button k-button-sm k-rounded-md k-button-solid k-button-solid-base"
                                                    onClick={() => {
                                                        setEditingDealerNoteIndex(null);
                                                        setEditedDealerNoteText('');
                                                        setOriginalDealerNoteContent('');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        // View mode - keep original layout
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ marginBottom: "8px" }}>
                                                    <strong>Note:</strong>
                                                    <p style={{ margin: "5px 0", lineHeight: "1.4" }}>{note.note}</p>
                                                </div>
                                                <div style={{ fontSize: "12px", color: "#666" }}>
                                                    <span style={{ marginRight: "15px" }}>
                                                        <strong>Created:</strong> {note.created_at}
                                                    </span>
                                                    <span>
                                                        <strong>By:</strong> {note.created_by}
                                                    </span>
                                                </div>
                                            </div>
                                            <div style={{ marginLeft: "10px" }}>
                                                <div style={{ display: "flex", gap: "8px" }}>
                                                    <Button
                                                        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
                                                        style={{
                                                            backgroundColor: "#007bff",
                                                            borderColor: "#007bff",
                                                            color: "white"
                                                        }}
                                                        onClick={() => {
                                                            setEditingDealerNoteIndex(index);
                                                            setEditedDealerNoteText(note.note);
                                                            setOriginalDealerNoteContent(note.note);
                                                        }}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <span
                                                        className={'k-icon k-i-trash'}
                                                        style={{ 
                                                            color: '#dc3545', 
                                                            fontSize: '16px', 
                                                            cursor: 'pointer',
                                                            padding: '8px',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}
                                                        onClick={() => handleDeleteDealerNote(note.created_at, note.note, index)}
                                                        title="Delete note"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <p>No dealer notes available.</p>
                        )}
                    </div>
                    <DialogActionsBar>
                        <Button
                            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                            onClick={() => {
                                setDealerNotesModalVisible(false);
                                setShowAddDealerNoteInModal(false);
                                setNewDealerNoteTextInModal('');
                                setSelectedDealerForNoteId(null);
                            }}
                        >
                            Close
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
}

export default DealerFormScreen;
